import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, Lightbulb, BarChart3, Calendar, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface DecisionLifecycleBlockProps {
  reviewDate?: string;
  validityDays?: number;
  sessionId?: string;
  committedAt?: string;
}

export function DecisionLifecycleBlock({ reviewDate, validityDays = 30, sessionId, committedAt }: DecisionLifecycleBlockProps) {
  const navigate = useNavigate();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });
    } catch { return dateStr; }
  };

  const displayReviewDate = formatDate(reviewDate) || (committedAt
    ? formatDate(new Date(new Date(committedAt).getTime() + validityDays * 86400000).toISOString())
    : `in ${validityDays} Tagen`);

  return (
    <Card id="lifecycle" className="scroll-mt-24 border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-primary/5">
      <CardContent className="pt-6 space-y-5">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">Decision Lifecycle</h2>
          <Badge variant="outline" className="text-[10px] ml-auto">
            Active
          </Badge>
        </div>

        <p className="text-sm text-muted-foreground">
          Quentari begleitet diese Entscheidung bis zum Review. Hypothesen und KPIs werden kontinuierlich überwacht.
        </p>

        {/* Review Date & Validity */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-3">
            <Calendar className="h-5 w-5 text-primary shrink-0" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Review-Datum</p>
              <p className="text-sm font-semibold">{displayReviewDate}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-3">
            <RefreshCw className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Validierungsfenster</p>
              <p className="text-sm font-semibold">{validityDays} Tage</p>
            </div>
          </div>
        </div>

        {/* Action Links */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1 justify-start gap-2"
            onClick={() => navigate("/app/hypotheses")}
          >
            <Lightbulb className="h-4 w-4 text-amber-500" />
            Hypothesen tracken
            <ArrowRight className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>
          <Button
            variant="outline"
            className="flex-1 justify-start gap-2"
            onClick={() => navigate("/dashboard")}
          >
            <BarChart3 className="h-4 w-4 text-primary" />
            KPI Dashboard
            <ArrowRight className="h-3 w-3 ml-auto text-muted-foreground" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
